import { useEffect, useState, useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useLanguage } from "./LanguageContext";
import { Reveal } from "./Reveal";
import { WEDDING } from "./config";
import { IznikRosette, IznikTexture } from "./Motifs";

function getRemaining(target) {
  const diff = target - Date.now();
  if (diff <= 0) return null;
  return {
    days: Math.floor(diff / 86400000),
    hours: Math.floor((diff / 3600000) % 24),
    mins: Math.floor((diff / 60000) % 60),
    secs: Math.floor((diff / 1000) % 60),
  };
}

function Unit({ value, label }) {
  const display = String(value).padStart(2, "0");
  return (
    <div className="flex flex-col items-center" data-testid={`countdown-${label}`}>
      <span className="font-display text-6xl md:text-8xl lg:text-[7.5rem] font-light leading-none tabular-nums text-cream">
        {display}
      </span>
      <span className="mt-4 h-px w-8 bg-gold/50" />
      <span className="mt-4 font-sans text-[10px] md:text-xs uppercase tracking-[0.32em] text-cream/70">
        {label}
      </span>
    </div>
  );
}

export default function Countdown() {
  const { t } = useLanguage();
  const target = new Date(WEDDING.dateISO).getTime();
  const [time, setTime] = useState(() => getRemaining(target));
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const rotate = useTransform(scrollYProgress, [0, 1], [0, 60]);

  useEffect(() => {
    const id = setInterval(() => setTime(getRemaining(target)), 1000);
    return () => clearInterval(id);
  }, [target]);

  const labels = {
    days: t.countdown.days,
    hours: t.countdown.hours,
    mins: t.countdown.mins,
    secs: t.countdown.secs,
  };

  return (
    <section
      ref={ref}
      data-testid="countdown-section"
      className="relative bg-turq py-24 md:py-36 overflow-hidden"
    >
      <IznikTexture dark size={300} opacity={0.10} />
      <motion.div
        style={{ rotate }}
        className="pointer-events-none absolute -right-32 -top-32 opacity-[0.12]"
        aria-hidden="true"
      >
        <IznikRosette className="w-[460px] h-[460px]" color="#FFFFFF" accent="#C5A059" />
      </motion.div>
      <motion.div
        style={{ rotate }}
        className="pointer-events-none absolute -left-40 -bottom-40 opacity-[0.10]"
        aria-hidden="true"
      >
        <IznikRosette className="w-[520px] h-[520px]" color="#FFFFFF" accent="#FF5E8E" />
      </motion.div>

      <div className="relative z-10 mx-auto max-w-5xl px-6 md:px-10 text-center">
        <Reveal>
          <span className="font-sans text-xs uppercase tracking-[0.3em] text-blush">
            03 — {t.countdown.kicker}
          </span>
          <h2 className="mt-4 font-display text-3xl md:text-5xl italic text-cream/95">
            {t.countdown.title}
          </h2>
          <span className="mx-auto mt-8 block h-px w-16 bg-gold/50" />
        </Reveal>

        <Reveal delay={0.15}>
          {time ? (
            <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-y-12 gap-x-4">
              <Unit value={time.days} label={labels.days} />
              <Unit value={time.hours} label={labels.hours} />
              <Unit value={time.mins} label={labels.mins} />
              <Unit value={time.secs} label={labels.secs} />
            </div>
          ) : (
            <p className="mt-14 font-display text-4xl md:text-6xl italic text-cream">
              {t.countdown.done}
            </p>
          )}
        </Reveal>
      </div>
    </section>
  );
}
